import React from 'react';
import { FeatureFlagName, getFeatureFlags } from '@uplift/api';
import { useQuery } from '@tanstack/react-query';
import { useCurrentuser } from '@uplift/contexts';


/**
 * ⛔️ use `useFeatureFlags` hook for getting Feature flags
*/
export const useUserFeatureFlagsQuery = (checkImpersonated = false) => {
  const me = useCurrentuser();

  return useQuery({
    queryKey: [ 'user-feature-flags', me?.id, checkImpersonated ],
    queryFn: async () => {
      const response = await getFeatureFlags(checkImpersonated);

      return response.data;
    },
    keepPreviousData: true,
    staleTime: Infinity,
    cacheTime: Infinity,
  });
};


/**
 * - default value for feature flags is `false`, so while it is loading from server all ff is `false`
 * - function will return `false` event if feature flag is no presented in the database
*/
export const useFeatureFlags = <T extends FeatureFlagName>(flags: T[], checkImpersonated = false) => {
  const { data: allFlags, isLoading } = useUserFeatureFlagsQuery(checkImpersonated);

  return React.useMemo(() => {
    return {
      ...flags.reduce((prev, curr) => ({
        ...prev,
        [curr]: allFlags?.[curr] ?? null,
      }), {} as Partial<Record<T, boolean | null>>),
      isLoading
    };
  }, [ flags, isLoading, allFlags ]);
};


/**
 * returns single flag state `true/false` for flag name
*/
export const useFeatureFlag = (flag: FeatureFlagName, checkImpersonated = false) => {
  return useFeatureFlags([ flag ], checkImpersonated)[flag];
};
